/*
 * accessible-autoplay-video.js
 * ---------------------------------------------------------------------------
 * Accessibility for the theme's autoplaying videos. Three behaviours:
 *
 * A) prefers-reduced-motion (WCAG 2.3.3). When the visitor has the OS/browser
 *    "Reduce motion" setting on, NO autoplaying video is allowed to move:
 *      - native <video autoplay> are paused and frozen on their first frame
 *        (the autoplay attribute is removed so they can't restart);
 *      - Vimeo/YouTube autoplay iframes are reloaded with autoplay/background
 *        stripped so they sit on a static poster;
 *      - a capturing 'play' listener re-pauses managed videos if anything (the
 *        hero slider's playVideoInSlide, nav-hover .play(), etc.) tries to
 *        (re)start them — so we don't have to edit the vendor _sm-theme.js.
 *    Users can still opt in to a video via the pause/play toggle (below).
 *
 * B) Pause/Stop control (WCAG 2.2.2). Looping background videos otherwise have
 *    no mechanism to stop them. Each background-media wrapper carries the class
 *    `.js-autoplay-bg-wrapper`; an accessible pause/play <button> is injected
 *    into each wrapper and controls every <video>/<iframe> inside it (covering
 *    the desktop+mobile pair with a single toggle positioned over the media).
 *
 * C) Accessible name for the existing hero slideshow toggles (WCAG 4.1.2). The
 *    vendor hero play/pause buttons had no accessible name; this keeps their
 *    aria-label/aria-pressed in sync with the play/pause state.
 *
 * No external dependencies. ES5 syntax for broad compatibility.
 */
(function () {
  'use strict';

  var mediaQuery = '(prefers-reduced-motion: reduce)';
  var mq = window.matchMedia ? window.matchMedia(mediaQuery) : null;
  var wrapperSelector = '.js-autoplay-bg-wrapper';
  var mediaSelector = 'video, iframe';

  // Per-toggle "is a video currently visible?" checks, re-run on resize so the
  // toggle hides/shows as the viewport crosses a section's media-swap breakpoint.
  var toggleUpdaters = [];

  function reduced() {
    return !!(mq && mq.matches);
  }

  function each(list, fn) {
    if (list) {
      Array.prototype.forEach.call(list, fn);
    }
  }

  // Root-inclusive querySelectorAll: matches the root element itself as well as
  // its descendants, so nodes injected bare (Swiper clones, app blocks) are
  // covered, not just nodes injected as wrapped subtrees.
  function queryAll(root, selector) {
    var out = [];
    if (root.matches && root.matches(selector)) { out.push(root); }
    if (root.querySelectorAll) {
      Array.prototype.push.apply(out, root.querySelectorAll(selector));
    }
    return out;
  }

  function isVimeo(frame) {
    return /player\.vimeo\.com/.test(frame.src || '');
  }

  // --- A. prefers-reduced-motion ------------------------------------------

  function neutralizeVideo(video) {
    var firstTime = !video.dataset.autoplayManaged;
    video.dataset.autoplayManaged = '1'; // remember it was an autoplay video
    video.autoplay = false;
    video.removeAttribute('autoplay');
    // Decorative videos can always be paused. A user-operable <video controls>
    // (e.g. the PDP gallery) is paused only on first sight — enough to stop the
    // initial autoplay — so a later re-run doesn't interrupt deliberate playback.
    if (firstTime || !video.hasAttribute('controls')) {
      try { video.pause(); } catch (e) {}
    }
  }

  function neutralizeIframe(frame) {
    var src = frame.getAttribute('src') || '';
    if (src.indexOf('autoplay=1') === -1 && src.indexOf('background=1') === -1) {
      return;
    }
    // Strip both: with Vimeo background=1, the player autoplays regardless of
    // the autoplay flag, so both must go for the motion to actually stop.
    var next = src.replace(/autoplay=1/g, 'autoplay=0').replace(/background=1/g, 'background=0');
    if (next !== src) {
      frame.dataset.autoplayManaged = '1';
      frame.setAttribute('src', next); // reload onto a static, paused poster
    }
  }

  function applyReducedMotion(root) {
    if (!reduced()) return;
    var scope = root || document;
    each(queryAll(scope, 'video[autoplay], video[data-autoplay-managed]'), neutralizeVideo);
    each(queryAll(scope, 'iframe[src*="autoplay=1"], iframe[src*="background=1"]'), neutralizeIframe);
  }

  // Re-pause any managed autoplay video that something tries to (re)start while
  // reduced-motion is on — unless the user opted in via the toggle (allowPlay).
  // Never fight a <video controls>: playing it is a deliberate user action and
  // must be allowed even under reduced motion.
  document.addEventListener('play', function (e) {
    var v = e.target;
    if (!reduced() || !v || v.tagName !== 'VIDEO') return;
    if (v.hasAttribute('controls') || v.dataset.allowPlay) return;
    var managed = v.dataset.autoplayManaged || v.hasAttribute('autoplay');
    if (managed) {
      try { v.pause(); } catch (err) {}
    }
  }, true);

  // --- B. pause/stop control (WCAG 2.2.2) ---------------------------------

  // Reuse the hero control's icons (assets/pause-icon.svg, play-icon.svg),
  // passed in by the loader snippet via window.iwAutoplayIcons so all
  // pause/play buttons share one source. Inline fallbacks cover the unlikely
  // case the global is missing. CSS (.autoplay-toggle svg) sizes them.
  var icons = window.iwAutoplayIcons || {};
  var pauseIcon = icons.pause || '<svg viewBox="0 0 25 25" aria-hidden="true" focusable="false"><rect x="2" width="8" height="25" rx="2" fill="currentColor"/><rect x="15" width="8" height="25" rx="2" fill="currentColor"/></svg>';
  var playIcon = icons.play || '<svg viewBox="0 0 25 25" aria-hidden="true" focusable="false"><path d="M20.9 10.6c1.8 1 1.8 3.7 0 4.7l-14.3 8.3c-1.8 1-4.1-.3-4.1-2.4V4.7c0-2.1 2.3-3.4 4.1-2.4z" fill="currentColor"/></svg>';

  function setToggleState(btn, paused) {
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    btn.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
    btn.classList.toggle('is-paused', paused); // drives play/pause icon sizing
    btn.innerHTML = paused ? playIcon : pauseIcon;
  }

  function controlMedia(media, action) {
    each(media, function (m) {
      if (m.tagName === 'VIDEO') {
        if (action === 'pause') {
          delete m.dataset.allowPlay;
          try { m.pause(); } catch (e) {}
        } else {
          m.dataset.allowPlay = '1'; // let the reduced-motion guard allow this play
          var p = m.play();
          if (p && p.catch) { p.catch(function () {}); }
        }
      } else if (isVimeo(m)) {
        // Vimeo accepts postMessage commands without loading the player SDK.
        try { m.contentWindow.postMessage(JSON.stringify({ method: action }), '*'); } catch (e) {}
      }
    });
  }

  // A media element counts as visible when it generates layout boxes — false for
  // anything display:none'd up the tree. This reads whatever the CSS decided, so
  // it needs no knowledge of the section's breakpoint.
  function hasVisibleMedia(wrapper) {
    var media = wrapper.querySelectorAll(mediaSelector);
    for (var i = 0; i < media.length; i++) {
      if (media[i].getClientRects().length) return true;
    }
    return false;
  }

  function buildToggle(wrapper) {
    if (!wrapper.querySelectorAll(mediaSelector).length) return;
    // Per-section opt-out. WCAG 2.2.2 exempts auto-playing media that lasts
    // <=5s, so a section may set data-autoplay-toggle="off" on the wrapper to
    // omit the control for a short/decorative video. Default (attribute absent)
    // keeps the toggle.
    if (wrapper.getAttribute('data-autoplay-toggle') === 'off') return;
    if (wrapper.querySelector('.autoplay-toggle')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'autoplay-toggle';
    wrapper.appendChild(btn);

    if (window.getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }

    // Initial state: if reduced-motion already paused the media, show "play".
    var paused = reduced();
    setToggleState(btn, paused);

    btn.addEventListener('click', function () {
      paused = !paused;
      // Control the whole desktop+mobile pair so the off-breakpoint video stops too.
      controlMedia(wrapper.querySelectorAll(mediaSelector), paused ? 'pause' : 'play');
      setToggleState(btn, paused);
    });

    // Only surface the toggle while a video/iframe is actually rendered for the
    // current breakpoint. A section can use video on one breakpoint and an image
    // on the other; the off-breakpoint <video> stays in the DOM (display:none),
    // so without this an orphaned toggle would sit over the static image.
    function update() {
      btn.style.display = hasVisibleMedia(wrapper) ? '' : 'none';
    }
    update();
    toggleUpdaters.push(update);
  }

  // Merchant poster fallback for video_tag background videos (shown until
  // playback, per the standard <video poster> behaviour — we only set the
  // attribute). Native <video> sections set poster="" directly in Liquid; the
  // video_tag filter can't emit a custom poster, so those wrappers carry the
  // poster URLs as data-poster-desktop/-mobile and each video is tagged with an
  // explicit JS-hook class (js-autoplay-poster-desktop/-mobile) — never a
  // styling class — to say which one it is.
  function setPoster(wrapper, selector, url) {
    if (!url) return; // merchant left it blank — keep video_tag's auto poster
    each(wrapper.querySelectorAll(selector), function (v) {
      // Override the auto-generated poster video_tag emits with the merchant's.
      if (v.tagName === 'VIDEO') {
        v.setAttribute('poster', url);
      }
    });
  }

  function applyPosters(wrapper) {
    setPoster(wrapper, '.js-autoplay-poster-desktop', wrapper.getAttribute('data-poster-desktop'));
    setPoster(wrapper, '.js-autoplay-poster-mobile', wrapper.getAttribute('data-poster-mobile'));
  }

  // One pass per .js-autoplay-bg-wrapper: apply posters, then inject the toggle
  // (buildToggle dedupes and skips wrappers with no video/iframe).
  function setupToggles(root) {
    var scope = root || document;
    each(queryAll(scope, wrapperSelector), function (wrapper) {
      applyPosters(wrapper);
      buildToggle(wrapper);
    });
  }

  // --- C. accessible name for the existing hero slideshow toggles ---------
  // The hero play/pause buttons (.js-hero-slider-toggle) are owned by the
  // vendor _sm-theme.js, which only flips the .pause/.play icon visibility. Keep
  // their aria-label/aria-pressed in step with that state so screen-reader users
  // hear the current action. Selected by class (not id) so every hero instance
  // on the page is covered. Initial state is set in the section markup.
  function syncSliderToggle(btn) {
    var playEl = btn.querySelector('.play');
    var paused = !!(playEl && window.getComputedStyle(playEl).display !== 'none');
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    btn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
  }

  function enhanceSliderToggles() {
    each(document.querySelectorAll('.js-hero-slider-toggle'), function (btn) {
      if (btn.dataset.a11ySynced) return;
      btn.dataset.a11ySynced = '1';
      // Run after the vendor click handler has flipped icon visibility.
      btn.addEventListener('click', function () {
        window.setTimeout(function () { syncSliderToggle(btn); }, 0);
      });
      syncSliderToggle(btn);
    });
  }

  // --- init ---------------------------------------------------------------

  function run(root) {
    applyReducedMotion(root);
    setupToggles(root);
  }

  function start() {
    run(document);
    enhanceSliderToggles();

    // Catch media injected after load (sliders, app blocks, theme editor).
    if (window.MutationObserver) {
      var observer = new MutationObserver(function (mutations) {
        each(mutations, function (m) {
          each(m.addedNodes, function (node) {
            if (node.nodeType === 1) { run(node); }
          });
        });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Re-check toggle visibility when the viewport crosses a media-swap
    // breakpoint (debounced — we only read layout, never write it).
    var resizeTimer;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        each(toggleUpdaters, function (fn) { fn(); });
      }, 150);
    });

    // React if the visitor toggles "reduce motion" after load.
    if (mq) {
      var onChange = function () { run(document); };
      if (mq.addEventListener) { mq.addEventListener('change', onChange); }
      else if (mq.addListener) { mq.addListener(onChange); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
